import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Megaphone } from "lucide-react";
import { Avatar } from "@/components/common";
import { useEvent, useEvents } from "@/context/AppContext";
import { api } from "@/lib/api";
import { CONV_EVENTS } from "@/lib/inbox";
import { fmtListTime } from "@/lib/format";

const isNew = (c) => c.last_direction !== "out" && c.last_message_at && (!c.site_seen_at || new Date(c.last_message_at) > new Date(c.site_seen_at));
// a channel with a new message goes first ('NUOVO'), the others keep the order chosen by the owner
const byLast = (a, b) => (isNew(b) - isNew(a)) || (isNew(a) ? new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0) : (a.order ?? 1e6) - (b.order ?? 1e6));

// Channels visible on the site for the selected profile(s): one scrolling row above the chats, newest message first.
export function ChannelStrip({ accountIds, accounts, activeId, open }) {
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const key = accountIds.join(",");
  const load = useCallback(() => {
    if (!key) { setItems([]); return; }
    api.get("/conversations", { params: { account_ids: key, kind: "channel", inbox: true, limit: 100 }, timeout: 9000 })
      .then((r) => setItems(r.data.items.filter((c) => accountIds.includes(c.account_id)).sort(byLast))).catch(() => {});
  }, [key]); // eslint-disable-line
  useEffect(() => { setItems([]); load(); }, [load]);
  const upsert = (c) => {
    if (!c || c.kind !== "channel" || !accountIds.includes(c.account_id)) return;
    if (c.site_hidden || c.inactive) { setItems((p) => p.filter((x) => x.id !== c.id)); return; }
    setItems((p) => [{ ...(p.find((x) => x.id === c.id) || {}), ...c }, ...p.filter((x) => x.id !== c.id)].sort(byLast));
  };
  useEvent("message.new", (d) => upsert(d.conversation));
  useEvents(CONV_EVENTS, (d) => upsert(d.conversation));
  useEvent("rt.resync", load);
  useEvent("TELEGRAM_FOLDER_VISIBILITY_UPDATED", load);
  useEvent("CHANNELS_VISIBILITY_UPDATED", load);
  useEvent("MESSAGES_ACCOUNT_LIST_UPDATED", load);
  useEvent("CHANNEL_SEEN", (d) => setItems((p) => p.map((x) => (x.id === d.conversation_id ? { ...x, site_seen_at: d.site_seen_at } : x)).sort(byLast)));
  if (!items.length) return null;
  const many = accountIds.length > 1;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 220, behavior: "smooth" });
  const label = many ? "" : ` · @${accounts.find((a) => a.id === accountIds[0])?.username || ""}`;
  return (
    <div className="shrink-0 border-b border-[#232738] px-2.5 py-2" data-testid="channel-strip">
      <div className="mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        <Megaphone className="h-3 w-3 text-sky-400" /><span className="truncate" data-testid="channel-strip-title">Canali{label}</span>
        <button onClick={() => scroll(-1)} className="ml-auto p-0.5 rounded hover:bg-white/5 hover:text-white" data-testid="channel-strip-left"><ChevronLeft className="h-3.5 w-3.5" /></button>
        <button onClick={() => scroll(1)} className="p-0.5 rounded hover:bg-white/5 hover:text-white" data-testid="channel-strip-right"><ChevronRight className="h-3.5 w-3.5" /></button>
      </div>
      <div ref={ref} className="flex gap-1.5 overflow-x-auto no-scrollbar scroll-smooth" data-testid="channel-strip-row">
        {items.map((c) => {
          const acc = accounts.find((a) => a.id === c.account_id);
          const unread = isNew(c);
          return (
            <button key={c.id} onClick={() => { open(c.id); if (unread) api.post(`/conversations/${c.id}/channel-seen`).catch(() => {}); }} title={c.channel_title} data-testid={`channel-card-${c.id}`} data-unread={unread ? "1" : "0"}
              className={`relative shrink-0 w-[92px] rounded-lg border p-1.5 text-left transition-colors ${c.id === activeId ? "bg-violet-600/15 border-violet-500" : unread ? "border-red-500 bg-red-500/[0.06]" : "border-[#232738] hover:border-violet-500/50"}`}>
              <div className="flex items-center gap-1">
                <span className="relative"><Avatar name={c.channel_title} size={22} />{many && <span className="absolute -bottom-1 -right-1"><Avatar name={acc?.username || acc?.label} url={acc?.avatar_url} size={12} /></span>}</span>
                {unread && <span className="ml-auto rounded bg-red-600 px-1 text-[8px] font-bold text-white" data-testid={`channel-card-new-${c.id}`}>NUOVO</span>}
              </div>
              <div className="mt-1 truncate text-[11px] font-semibold text-slate-200" data-testid={`channel-card-title-${c.id}`}>{c.channel_title}</div>
              <div className="text-[9px] text-slate-500" data-testid={`channel-card-time-${c.id}`}>{fmtListTime(c.last_message_at)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
